/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink as RouterLink } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { Theme as MaterialUITheme } from '@rjsf/material-ui';
import { Typography } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import Button from 'components/common/Button';
import { fieldTemplate, objectFieldTemplate, widgets, fields } from '../../submission/templates';
import withTheme from '../../submission/rjsf/withTheme';
import { OpenMode } from './openMode';

const MuiForm = withTheme(MaterialUITheme);

const useStyles = makeStyles(() => ({
  heading: {
    display: 'flex',
    flexFlow: 'row wrap',
    justifyContent: 'space-between',
  },
  description: {
    maxWidth: 600,
  },
  buttons: {
    display: 'flex',
    flex: '1',
    flexFlow: 'row wrap',
    justifyContent: 'flex-end',
    margin: '1rem',
  },
  editButton: {
    margin: '0.5rem',
    padding: '1rem',
  },
  deleteButton: {
    margin: '0.5rem',
    padding: '1rem',
  },
  buttonIcon: {
    marginRight: '0.5rem',
  },
}));

export default function ContactForm({ openMode, contact, onChange, onDelete, uiSchema, jsonSchema }) {
  const AdapterLink = React.forwardRef((prop, ref) => <RouterLink innerRef={ref} {...prop} />);
  const idPrefix = 'contact';
  const { t } = useTranslation();

  const classes = useStyles();

  const onFormChange = form => onChange(form);
  const transformErrors = errors => errors;
  const onError = () => {
    // not implemented yet
  };

  let description;
  switch (openMode) {
    case OpenMode.NEW:
      description = t('messages.contact.add.this.page');
      break;
    case OpenMode.VIEW:
      description = t('messages.contact.view.this.page');
      break;
    case OpenMode.EDIT:
      description = t('messages.contact.edit.this.page');
      break;
    default:
      // We should store this kind of errors in a log system (like Sentry for example)
      // They should not be displayed to the final users
      throw new Error(`unknown OpenMode ${openMode}, switch case not exhausted`);
  }

  return (
    <>
      <div className={classes.heading}>
        <div>
          <Typography data-testid={`${idPrefix}.title`} variant="h1" className={classes.title}>
            {t('contact.title')}
          </Typography>
          <Typography data-testid="contact.description" variant="body1" className={classes.description}>
            {description}
          </Typography>
        </div>
        <div className={classes.buttons}>
          <Button
            className={classes.deleteButton}
            startIcon={<DeleteIcon />}
            onClick={onDelete}
            disabled={openMode === OpenMode.NEW}
          >
            {t('contact.delete')}
          </Button>
          <Button
            className={classes.editButton}
            startIcon={<EditIcon />}
            to={`/addressbook/contact/${contact?.id || ''}/edit`}
            component={AdapterLink}
            disabled={openMode !== OpenMode.VIEW}
          >
            {t('contact.edit')}
          </Button>
        </div>
      </div>
      <MuiForm
        translation={t}
        className={classes.form}
        idPrefix={idPrefix}
        id={idPrefix}
        schema={jsonSchema}
        uiSchema={uiSchema}
        ObjectFieldTemplate={objectFieldTemplate}
        FieldTemplate={fieldTemplate}
        fields={fields}
        widgets={widgets}
        formData={contact}
        disabled={openMode === OpenMode.VIEW}
        onChange={onFormChange}
        onError={onError}
        showErrorList={false}
        validateOnBlur={true}
        transformErrors={transformErrors}
        omitExtraData={false}
        liveOmit={true}
        liveValidate={openMode !== OpenMode.VIEW}
        noHtml5Validate
      >
        <></>
      </MuiForm>
    </>
  );
}
